import React, { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import Select from 'react-select';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

// Toast notifications (adjust to your toast system)
import { toast } from 'react-hot-toast';

// UI components (adjust paths as needed)
import Button from '../../../shared/components/ui/Button';

// === API Functions (adjust paths accordingly) ===

import {
  getDynamicFields,
  getDynamicFieldsProject,
  getDynamicFieldsUnit,
  getDynamicFieldsUnitPricing,
  getAnnexures,
  updateTemplate,
  getTemplateById
} from '../../services/api/implementation/impl';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';

const EditDocumentTemplate = ({ templateid, onClose, onSuccess }) => {
  const userId = Cookies.get('userId');
  // ---------- 1) Fetch the Template Data ----------
  const {
    data: templateData,
    isLoading: isLoadingTemplate,
    isError: isErrorTemplate
  } = useQuery({
    queryKey: ['template', templateid],
    queryFn: () => getTemplateById(templateid),
    enabled: !!templateid
  });

  // ---------- 2) Fetch Dynamic Fields ----------
  // Expected format:
  // [ { id: 1, column_name: 'CLOSED_DEAL_ID', readable_name: 'Closed Deal Id' }, ... ]
  const {
    data: dynamicFieldsList,
    isLoading: isLoadingDynamicFields,
    isError: isErrorDynamicFields
  } = useQuery({
    queryKey: ['dynamic-fields'],
    queryFn: getDynamicFields
  });

  // ---------- 3) Fetch Annexures ----------
  // Expected format:
  // [ { id: 1, column_name: "PROJECT_SPECIFICATION", readable_name: "Project Specification" }, ... ]
  const {
    data: annexureList,
    isLoading: isLoadingAnnexures,
    isError: isErrorAnnexures
  } = useQuery({
    queryKey: ['annexures'],
    queryFn: getAnnexures
  });

  const {
    data: projectDetailsFieldsList,
    isLoading: isLoadingProjectDetailsFields,
    isError: isErrorProjectDetailsFields
  } = useQuery({
    queryKey: ['dynamic-fields', 'project'],
    queryFn: getDynamicFieldsProject
  });

  const {
    data: unitDetailsFieldsList,
    isLoading: isLoadingUnitDetailsFields,
    isError: isErrorUnitDetailsFields
  } = useQuery({
    queryKey: ['dynamic-fields', 'unit'],
    queryFn: getDynamicFieldsUnit
  });

  const {
    data: unitPricingDetailsFieldsList,
    isLoading: isLoadingUnitPricingDetailsFields,
    isError: isErrorUnitPricingDetailsFields
  } = useQuery({
    queryKey: ['dynamic-fields', 'unit-pricing'],
    queryFn: getDynamicFieldsUnitPricing
  });

  // ---------- 4) Component States ----------
  const [documentName, setDocumentName] = useState('');
  const [description, setDescription] = useState('');
  const [customDetails, setCustomDetails] = useState('');
  const [includeLogo, setIncludeLogo] = useState(true);
  const [includeWatermark, setIncludeWatermark] = useState(false);
  const [selectedDynamicFields, setSelectedDynamicFields] = useState([]); // For react‑select
  const [selectedProjectDetails, setSelectedProjectDetails] = useState([]);
  const [selectedUnitDetails, setSelectedUnitDetails] = useState([]);
  const [selectedUnitPricingDetails, setSelectedUnitPricingDetails] = useState([]);
  const [selectedAnnexures, setSelectedAnnexures] = useState([]); // For react‑select

  // ---------- 5) Build React‑Select Options ----------
  const dynamicFieldOptions = useMemo(() => {
    if (!dynamicFieldsList || !Array.isArray(dynamicFieldsList)) return [];
    return dynamicFieldsList.map((field) => ({
      value: field.column_name,
      label: field.readable_name
    }));
  }, [dynamicFieldsList]);

  const annexureOptions = useMemo(() => {
    if (!annexureList || !Array.isArray(annexureList)) return [];
    return annexureList.map((ann) => ({
      value: ann.column_name,
      label: ann.readable_name
    }));
  }, [annexureList]);

  const projectDetailsOptions = useMemo(() => {
    if (!projectDetailsFieldsList || !Array.isArray(projectDetailsFieldsList)) return [];
    return projectDetailsFieldsList.map((field) => ({
      value: field.column_name,
      label: field.readable_name
    }));
  }, [projectDetailsFieldsList]);

  const unitDetailsOptions = useMemo(() => {
    if (!unitDetailsFieldsList || !Array.isArray(unitDetailsFieldsList)) return [];
    return unitDetailsFieldsList.map((field) => ({
      value: field.column_name,
      label: field.readable_name
    }));
  }, [unitDetailsFieldsList]);

  const unitPricingDetailsOptions = useMemo(() => {
    if (!unitPricingDetailsFieldsList || !Array.isArray(unitPricingDetailsFieldsList)) return [];
    return unitPricingDetailsFieldsList.map((field) => ({
      value: field.column_name,
      label: field.readable_name
    }));
  }, [unitPricingDetailsFieldsList]);

  // ---------- 6) Pre-Fill the Form When Data Is Loaded ----------

  // Track page view
  useEffect(() => {
    mixpanel.track('Edit Document Template Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [userId]);

  useEffect(() => {
    if (!templateData) return;

    setDocumentName(templateData.document_name || '');
    setDescription(templateData.description || '');
    setCustomDetails(templateData.content || '');
    setIncludeLogo(templateData.include_logo !== false);
    setIncludeWatermark(Boolean(templateData.include_watermark));

    const columnsToSelectOptions = (columns, options) => {
      const list = Array.isArray(columns) ? columns : [];
      return list
        .map((val) => options.find((option) => option.value === val) || { value: val, label: val })
        .filter((option) => option?.value);
    };

    setSelectedDynamicFields(columnsToSelectOptions(templateData.dynamic_fields, dynamicFieldOptions));

    setSelectedProjectDetails(
      columnsToSelectOptions(templateData.project_dynamic_fields, projectDetailsOptions)
    );
    setSelectedUnitDetails(columnsToSelectOptions(templateData.unit_dynamic_fields, unitDetailsOptions));
    setSelectedUnitPricingDetails(
      columnsToSelectOptions(templateData.unit_pricing_dynamic_fields, unitPricingDetailsOptions)
    );

    const preSelectedAnnexures = (templateData?.annexure || [])
      .map((val) =>
        annexureOptions.find((option) => option?.value?.toLowerCase() === val?.toLowerCase())
      )
      .filter((option) => option);
    setSelectedAnnexures(preSelectedAnnexures);
  }, [
    templateData,
    dynamicFieldOptions,
    annexureOptions,
    projectDetailsOptions,
    unitDetailsOptions,
    unitPricingDetailsOptions
  ]);

  // ---------- 7) Update Template Mutation ----------
  const { mutate: mutateUpdateTemplate, isLoading: isUpdating } = useMutation({
    mutationFn: (payload) => updateTemplate(templateid, payload),
    onSuccess: () => {
      toast.success('Template updated successfully!');
      if (onSuccess) onSuccess();
      if (onClose) onClose();
    },
    onError: (error) => {
      console.error('Error updating template:', error);
      toast.error(error?.response?.data?.message || error?.message || 'Failed to update template');
    }
  });

  // ---------- 8) Save Handler ----------
  const handleSave = () => {
    if (!templateData) {
      toast.error('Template data not loaded yet.');
      return;
    }

    // Build the payload to update the template.
    // We re-use some values (project_id, company_id, html_template) from the loaded templateData.
    const payload = {
      id: templateid,
      document_name: documentName,
      description: description,
      dynamic_fields: selectedDynamicFields.map((item) => item.value),
      project_dynamic_fields:
        selectedProjectDetails.length > 0
          ? selectedProjectDetails.map((item) => item.value)
          : null,
      unit_dynamic_fields:
        selectedUnitDetails.length > 0 ? selectedUnitDetails.map((item) => item.value) : null,
      unit_pricing_dynamic_fields:
        selectedUnitPricingDetails.length > 0
          ? selectedUnitPricingDetails.map((item) => item.value)
          : null,
      annexure: selectedAnnexures.map((item) => item.value),
      content: customDetails,
      project_id: templateData.project_id,
      company_id: templateData.company_id,
      include_logo: includeLogo,
      include_watermark: includeWatermark
    };

    mutateUpdateTemplate(payload);
  };

  // ---------- 9) Cancel Handler ----------
  const handleCancel = () => {
    if (onClose) onClose();
  };

  // ---------- 10) Loading/Error States ----------
  const isLoadingAll =
    isLoadingTemplate ||
    isLoadingDynamicFields ||
    isLoadingAnnexures ||
    isLoadingProjectDetailsFields ||
    isLoadingUnitDetailsFields ||
    isLoadingUnitPricingDetailsFields;
  const isErrorAll =
    isErrorTemplate ||
    isErrorDynamicFields ||
    isErrorAnnexures ||
    isErrorProjectDetailsFields ||
    isErrorUnitDetailsFields ||
    isErrorUnitPricingDetailsFields;

  if (isLoadingAll) {
    return <div>Loading ...</div>;
  }
  if (isErrorAll) {
    return <div>Error loading data. Please try again later.</div>;
  }

  // ---------- 11) Render the Form ----------
  return (
    <div className="w-full p-4">
      <div className="flex justify-between items-center mb-4 border-b pb-2">
        <h2 className="text-xl font-semibold">Edit Document Template</h2>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleCancel} displayType="secondary">
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isUpdating}>
            {isUpdating ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>

      {/* Document Details */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div>
          <label className="font-semibold">Document Name</label>
          <input
            type="text"
            className="w-full p-2 border rounded mt-1"
            placeholder="Document Name..."
            value={documentName}
            onChange={(e) => setDocumentName(e.target.value)}
          />
        </div>
        <div>
          <label className="font-semibold">Description</label>
          <input
            type="text"
            className="w-full p-2 border rounded mt-1"
            placeholder="Short description..."
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>
      </div>

      <div className="mb-6 rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-2">
        <p className="text-sm font-semibold text-slate-700">PDF look</p>
        <label className="flex items-start gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            className="mt-1"
            checked={includeLogo}
            onChange={(e) => setIncludeLogo(e.target.checked)}
          />
          <span>
            Company logo in header
            <span className="block text-xs text-slate-500">From company settings</span>
          </span>
        </label>
        <label className="flex items-start gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            className="mt-1"
            checked={includeWatermark}
            onChange={(e) => setIncludeWatermark(e.target.checked)}
          />
          <span>
            Watermark on body
            <span className="block text-xs text-slate-500">
              Faded logo/name behind the agreement — optional
            </span>
          </span>
        </label>
      </div>

      {/* Dynamic Fields Multi‑Select */}
      <div className="mb-6">
        <label className="font-semibold block mb-2">Dynamic Fields</label>
        <Select
          isMulti
          value={selectedDynamicFields}
          onChange={(newVal) => setSelectedDynamicFields(newVal)}
          options={dynamicFieldOptions}
          placeholder="Select dynamic fields..."
          styles={{
            multiValue: (provided) => ({
              ...provided,
              backgroundColor: '#2A565E'
            }),
            multiValueLabel: (provided) => ({
              ...provided,
              color: '#fff'
            })
          }}
        />
      </div>

      {/* Project Details Multi‑Select */}
      <div className="mb-6">
        <label className="font-semibold block mb-2">Project Details</label>
        <Select
          isMulti
          value={selectedProjectDetails}
          onChange={(newVal) => setSelectedProjectDetails(newVal)}
          options={projectDetailsOptions}
          placeholder="Select project detail fields..."
          styles={{
            multiValue: (provided) => ({
              ...provided,
              backgroundColor: '#2A565E'
            }),
            multiValueLabel: (provided) => ({
              ...provided,
              color: '#fff'
            })
          }}
        />
      </div>

      {/* Unit Details Multi‑Select */}
      <div className="mb-6">
        <label className="font-semibold block mb-2">Unit Details</label>
        <Select
          isMulti
          value={selectedUnitDetails}
          onChange={(newVal) => setSelectedUnitDetails(newVal)}
          options={unitDetailsOptions}
          placeholder="Select unit detail fields..."
          styles={{
            multiValue: (provided) => ({
              ...provided,
              backgroundColor: '#2A565E'
            }),
            multiValueLabel: (provided) => ({
              ...provided,
              color: '#fff'
            })
          }}
        />
      </div>

      {/* Unit Pricing Details Multi‑Select */}
      <div className="mb-6">
        <label className="font-semibold block mb-2">Unit Pricing Details</label>
        <Select
          isMulti
          value={selectedUnitPricingDetails}
          onChange={(newVal) => setSelectedUnitPricingDetails(newVal)}
          options={unitPricingDetailsOptions}
          placeholder="Select unit pricing fields..."
          styles={{
            multiValue: (provided) => ({
              ...provided,
              backgroundColor: '#2A565E'
            }),
            multiValueLabel: (provided) => ({
              ...provided,
              color: '#fff'
            })
          }}
        />
      </div>

      {/* Annexure Multi‑Select */}
      <div className="mb-6">
        <label className="font-semibold block mb-2">Annexure (Additional Docs)</label>
        <Select
          isMulti
          value={selectedAnnexures}
          onChange={(newVal) => setSelectedAnnexures(newVal)}
          options={annexureOptions}
          placeholder="Select annexure documents..."
          styles={{
            multiValue: (provided) => ({
              ...provided,
              backgroundColor: '#2A565E'
            }),
            multiValueLabel: (provided) => ({
              ...provided,
              color: '#fff'
            })
          }}
        />
      </div>

      {/* Rich Text Editor for Template Body */}
      <div className="mb-6">
        <label className="font-semibold block mb-2">Custom Details (Body)</label>
        <ReactQuill
          theme="snow"
          value={customDetails}
          onChange={setCustomDetails}
          placeholder="Type or paste the main body of your template here..."
          className="bg-white"
        />
      </div>
    </div>
  );
};

export default EditDocumentTemplate;
